import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

import type { ReconciliationDocumentType } from '@/api/domains/reconciliation.types';

type State = {
  reconciliationFile: File | null;
  documentType: ReconciliationDocumentType;
  orderGuid: string;
  distributorId: number | null;
  reconciliationId: number | null;
  isUploadConfirmationDialogOpen: boolean;
  ordersPeriod: string[];
};

type Actions = {
  setReconciliationFile: (file: File) => void;
  setDocumentType: (documentType: ReconciliationDocumentType) => void;
  setOrderGuid: (orderGuid: string) => void;
  setDistributorId: (distributorId: number) => void;
  setReconciliationId: (reconciliationId: number) => void;
  openUploadConfirmationDialog: () => void;
  closeUploadConfirmationDialog: () => void;
  setOrdersPeriod: (ordersPeriod: string[]) => void;
};

type ReconciliationFileStore = State & {
  actions: Actions;
};

const initialState: State = {
  reconciliationFile: null,
  documentType: 'specification',
  orderGuid: '',
  distributorId: null,
  reconciliationId: null,
  isUploadConfirmationDialogOpen: false,
  ordersPeriod: [],
};

const useReconciliationFileStore = create<ReconciliationFileStore>()(
  devtools(
    set => ({
      ...initialState,

      actions: {
        setReconciliationFile: reconciliationFile => set({ reconciliationFile }, false, 'setReconciliationFile'),
        setDocumentType: documentType => set({ documentType }, false, 'setDocumentType'),
        setOrderGuid: orderGuid => set({ orderGuid }, false, 'setOrderGuid'),
        setDistributorId: distributorId => set({ distributorId }, false, 'setDistributorId'),
        setReconciliationId: reconciliationId => set({ reconciliationId }, false, 'setReconciliationId'),
        openUploadConfirmationDialog: () =>
          set({ isUploadConfirmationDialogOpen: true }, false, 'openUploadConfirmationDialog'),
        closeUploadConfirmationDialog: () =>
          set({ isUploadConfirmationDialogOpen: false }, false, 'closeUploadConfirmationDialog'),
        setOrdersPeriod: ordersPeriod => set({ ordersPeriod }, false, 'setOrdersPeriod'),
      },
    }),
    {
      name: 'reconciliation-file-store',
      enabled: import.meta.env.DEV,
    },
  ),
);

export const useReconciliationFile = () => useReconciliationFileStore(state => state.reconciliationFile);
export const useReconciliationDocumentType = () => useReconciliationFileStore(state => state.documentType);
export const useReconciliationOrderGuid = () => useReconciliationFileStore(state => state.orderGuid);
export const useReconciliationDistributorId = () => useReconciliationFileStore(state => state.distributorId);
export const useReconciliationId = () => useReconciliationFileStore(state => state.reconciliationId);
export const useIsUploadConfirmationDialogOpen = () =>
  useReconciliationFileStore(state => state.isUploadConfirmationDialogOpen);
export const useReconciliationOrdersPeriod = () => useReconciliationFileStore(state => state.ordersPeriod);
export const useReconciliationFileStoreActions = () => useReconciliationFileStore(state => state.actions);

export const resetReconciliationFileStore = () => useReconciliationFileStore.setState(initialState);
